import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://taskeye.com';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;
const IS_PRODUCTION = import.meta.env.VITE_ENV === 'production';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  noIndex?: boolean;
  jsonLd?: object;
}

export function SEO({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  noIndex = false,
  jsonLd,
}: SEOProps) {
  // In non-production environments, always block indexing and omit canonical
  // to prevent dev/staging from appearing in search results or causing duplicate content.
  const shouldIndex = IS_PRODUCTION && !noIndex;
  const canonicalUrl = IS_PRODUCTION && canonical ? `${BASE_URL}${canonical}` : undefined;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {!shouldIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="TaskEye" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data — production only */}
      {IS_PRODUCTION && jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
